import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { 
  Zap, Plus, Play, Pause, Settings, Trash2, Copy, ArrowRight,
  Mail, MessageSquare, Clock, Users, Filter, Target,
  CheckCircle, XCircle, MoreVertical, Sparkles, X, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const triggerTypes = [
  { id: 'form_submit', label: 'Form Submission', icon: CheckCircle },
  { id: 'page_visit', label: 'Page Visit', icon: Target },
  { id: 'email_open', label: 'Email Opened', icon: Mail },
  { id: 'lead_score', label: 'Lead Score Change', icon: Users },
  { id: 'time_based', label: 'Time-based', icon: Clock },
];

const actionTypes = [
  { id: 'send_email', label: 'Send Email', icon: Mail },
  { id: 'send_sms', label: 'Send SMS', icon: MessageSquare },
  { id: 'add_tag', label: 'Add Tag', icon: Filter },
  { id: 'update_score', label: 'Update Lead Score', icon: Target },
  { id: 'notify_team', label: 'Notify Team', icon: Users },
];

export default function Automation() {
  const { token } = useAuth();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newWorkflow, setNewWorkflow] = useState({
    name: '',
    description: '',
    trigger: 'form_submit',
    actions: ['send_email'],
    status: 'paused'
  });

  useEffect(() => {
    fetchWorkflows();
  }, [token]);

  const fetchWorkflows = async () => {
    try {
      const response = await fetch(`${API}/api/marketing/automations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWorkflows(data.automations || []);
      }
    } catch (error) {
      console.error('Error fetching workflows:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewWorkflow({
      name: '',
      description: '',
      trigger: 'form_submit',
      actions: ['send_email'],
      status: 'paused'
    });
    setEditingWorkflow(null);
  };

  const handleCreateWorkflow = async () => {
    if (!newWorkflow.name) {
      toast.error('Workflow name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/api/marketing/automations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newWorkflow)
      });

      if (response.ok) {
        const data = await response.json();
        setWorkflows([data.automation, ...workflows]);
        resetForm();
        setShowCreateModal(false);
        toast.success('Workflow created successfully');
      } else {
        toast.error('Failed to create workflow');
      }
    } catch (error) {
      toast.error('Error creating workflow');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateWorkflow = async () => {
    if (!newWorkflow.name) {
      toast.error('Workflow name is required');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API}/api/marketing/automations/${editingWorkflow.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newWorkflow)
      });

      if (response.ok) {
        setWorkflows(workflows.map(w => w.id === editingWorkflow.id ? { ...w, ...newWorkflow } : w));
        resetForm();
        setShowCreateModal(false);
        toast.success('Workflow updated successfully');
      } else {
        toast.error('Failed to update workflow');
      }
    } catch (error) {
      toast.error('Error updating workflow');
    } finally {
      setSaving(false);
    }
  };

  const getTriggerInfo = (triggerId) => {
    return triggerTypes.find(t => t.id === triggerId) || triggerTypes[0];
  };

  const getActionInfo = (actionId) => {
    return actionTypes.find(a => a.id === actionId) || actionTypes[0];
  };

  const toggleWorkflowStatus = async (workflow) => {
    const newStatus = workflow.status === 'active' ? 'paused' : 'active';
    
    try {
      const response = await fetch(`${API}/api/marketing/automations/${workflow.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setWorkflows(workflows.map(w => 
          w.id === workflow.id ? { ...w, status: newStatus } : w
        ));
        toast.success(`Workflow ${newStatus === 'active' ? 'activated' : 'paused'}`);
      }
    } catch (error) {
      toast.error('Failed to update workflow status');
    }
  };

  const duplicateWorkflow = async (workflow) => {
    try {
      const response = await fetch(`${API}/api/marketing/automations/${workflow.id}/duplicate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWorkflows([data.automation, ...workflows]);
        toast.success('Workflow duplicated');
      } else {
        toast.error('Failed to duplicate workflow');
      }
    } catch (error) {
      toast.error('Error duplicating workflow');
    }
  };

  const deleteWorkflow = async (workflowId) => {
    if (!window.confirm('Delete this workflow?')) return;

    try {
      const response = await fetch(`${API}/api/marketing/automations/${workflowId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        setWorkflows(workflows.filter(w => w.id !== workflowId));
        toast.success('Workflow deleted');
      } else {
        toast.error('Failed to delete workflow');
      }
    } catch (error) {
      toast.error('Error deleting workflow');
    }
  };

  const openEditModal = (workflow) => {
    setEditingWorkflow(workflow);
    setNewWorkflow({
      name: workflow.name || '',
      description: workflow.description || '',
      trigger: workflow.trigger || 'form_submit',
      actions: workflow.actions || ['send_email'],
      status: workflow.status || 'paused'
    });
    setShowCreateModal(true);
  };

  const addAction = () => {
    setNewWorkflow({
      ...newWorkflow,
      actions: [...newWorkflow.actions, 'send_email']
    });
  };

  const removeAction = (index) => {
    if (newWorkflow.actions.length > 1) {
      const updated = [...newWorkflow.actions];
      updated.splice(index, 1);
      setNewWorkflow({ ...newWorkflow, actions: updated });
    }
  };

  const updateAction = (index, value) => {
    const updated = [...newWorkflow.actions];
    updated[index] = value;
    setNewWorkflow({ ...newWorkflow, actions: updated });
  };

  const stats = {
    total: workflows.length,
    active: workflows.filter(w => w.status === 'active').length,
    totalRuns: workflows.reduce((sum, w) => sum + (w.runs || 0), 0),
    totalConversions: workflows.reduce((sum, w) => sum + (w.conversions || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="w-6 h-6 text-primary" />
            Automation
          </h1>
          <p className="text-muted-foreground">Create and manage marketing automation workflows</p>
        </div>
        <button 
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="marketing-btn-primary flex items-center gap-2"
          data-testid="create-workflow-btn"
        >
          <Plus className="w-4 h-4" />
          <span>Create Workflow</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">Total Workflows</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">Active</p>
          <p className="text-2xl font-bold text-emerald-400">{stats.active}</p>
        </div>
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">Total Runs</p>
          <p className="text-2xl font-bold">{stats.totalRuns.toLocaleString()}</p>
        </div>
        <div className="metric-card">
          <p className="text-xs text-muted-foreground">Conversions</p>
          <p className="text-2xl font-bold text-primary">{stats.totalConversions}</p>
        </div>
      </div>

      {/* Workflows List */}
      <div className="space-y-4">
        {workflows.map(workflow => {
          const trigger = getTriggerInfo(workflow.trigger);
          return (
            <div key={workflow.id} className="marketing-card hover:border-primary/30 transition-colors" data-testid={`workflow-${workflow.id}`}>
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* Workflow Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      workflow.status === 'active' ? 'bg-primary/20 text-primary' : 'bg-secondary text-muted-foreground'
                    }`}>
                      <Zap className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{workflow.name}</h3>
                      <p className="text-sm text-muted-foreground">{workflow.description}</p>
                    </div>
                  </div>

                  {/* Flow Visualization */}
                  <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-2">
                    <div className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-xs shrink-0">
                      <trigger.icon className="w-3 h-3" />
                      <span>{trigger.label}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    {(workflow.actions || []).map((actionId, i) => {
                      const action = getActionInfo(actionId);
                      return (
                        <React.Fragment key={i}>
                          <div className="flex items-center gap-1 px-3 py-1.5 bg-secondary rounded-lg text-xs shrink-0">
                            <action.icon className="w-3 h-3" />
                            <span>{action.label}</span>
                          </div>
                          {i < workflow.actions.length - 1 && (
                            <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>

                {/* Metrics */}
                <div className="flex items-center gap-6 text-center">
                  <div>
                    <p className="text-lg font-bold">{(workflow.runs || 0).toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Runs</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-emerald-400">{workflow.conversions || 0}</p>
                    <p className="text-xs text-muted-foreground">Conversions</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold">
                      {workflow.runs > 0 ? (((workflow.conversions || 0) / workflow.runs) * 100).toFixed(1) : 0}%
                    </p>
                    <p className="text-xs text-muted-foreground">Conv. Rate</p>
                  </div>
                </div>

                {/* Status & Actions */}
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                    workflow.status === 'active' 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {workflow.status}
                  </span>
                  <div className="flex items-center gap-1">
                    <button 
                      onClick={() => toggleWorkflowStatus(workflow)}
                      className="p-2 hover:bg-secondary rounded-lg transition-colors"
                      data-testid={`toggle-workflow-${workflow.id}`}
                      title={workflow.status === 'active' ? 'Pause workflow' : 'Activate workflow'}
                    >
                      {workflow.status === 'active' ? (
                        <Pause className="w-4 h-4 text-amber-400" />
                      ) : (
                        <Play className="w-4 h-4 text-emerald-400" />
                      )}
                    </button>
                    <button 
                      onClick={() => duplicateWorkflow(workflow)}
                      className="p-2 hover:bg-secondary rounded-lg transition-colors"
                      data-testid={`copy-workflow-${workflow.id}`}
                      title="Duplicate workflow"
                    >
                      <Copy className="w-4 h-4 text-muted-foreground" />
                    </button>
                    <button 
                      onClick={() => openEditModal(workflow)}
                      className="p-2 hover:bg-secondary rounded-lg transition-colors"
                      data-testid={`settings-workflow-${workflow.id}`}
                      title="Edit workflow"
                    >
                      <Settings className="w-4 h-4 text-muted-foreground" />
                    </button>
                    <button 
                      onClick={() => deleteWorkflow(workflow.id)}
                      className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                      data-testid={`delete-workflow-${workflow.id}`}
                      title="Delete workflow"
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Last Run */}
              <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Last run: {workflow.last_run || 'Never'}
                </span>
                {workflow.created_at && (
                  <span>Created: {new Date(workflow.created_at).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          );
        })}

        {workflows.length === 0 && (
          <div className="marketing-card text-center py-12">
            <Zap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">No workflows created yet</p>
            <button 
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="marketing-btn-primary"
            >
              Create Your First Workflow
            </button>
          </div>
        )}
      </div>

      {/* AI Suggestions */}
      <div className="marketing-card">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          AI Workflow Suggestions
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div 
            className="p-4 bg-secondary/50 rounded-lg border border-border hover:border-primary/50 transition-colors cursor-pointer"
            onClick={() => {
              setNewWorkflow({
                name: 'Birthday Campaign',
                description: 'Send personalized birthday wishes with special offers to increase engagement.',
                trigger: 'time_based',
                actions: ['send_email', 'add_tag'],
                status: 'paused'
              });
              setShowCreateModal(true);
            }}
          >
            <p className="font-medium mb-1">Birthday Campaign</p>
            <p className="text-sm text-muted-foreground mb-3">
              Send personalized birthday wishes with special offers to increase engagement.
            </p>
            <button className="text-primary text-sm flex items-center gap-1 hover:underline">
              Create Workflow <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div 
            className="p-4 bg-secondary/50 rounded-lg border border-border hover:border-primary/50 transition-colors cursor-pointer"
            onClick={() => {
              setNewWorkflow({
                name: 'Post-Purchase Follow-up',
                description: 'Automatically follow up with customers after purchase to gather reviews.',
                trigger: 'form_submit',
                actions: ['send_email', 'notify_team'],
                status: 'paused'
              });
              setShowCreateModal(true);
            }}
          >
            <p className="font-medium mb-1">Post-Purchase Follow-up</p>
            <p className="text-sm text-muted-foreground mb-3">
              Automatically follow up with customers after purchase to gather reviews.
            </p>
            <button className="text-primary text-sm flex items-center gap-1 hover:underline">
              Create Workflow <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Create/Edit Workflow Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold">
                {editingWorkflow ? 'Edit Workflow' : 'Create Workflow'}
              </h2>
              <button 
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="p-1 hover:bg-secondary rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Workflow Name *</label>
                <input
                  type="text"
                  value={newWorkflow.name}
                  onChange={(e) => setNewWorkflow({ ...newWorkflow, name: e.target.value })}
                  className="marketing-input w-full"
                  placeholder="Welcome Email Sequence"
                  data-testid="workflow-name-input"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Description</label>
                <textarea
                  value={newWorkflow.description}
                  onChange={(e) => setNewWorkflow({ ...newWorkflow, description: e.target.value })}
                  className="marketing-input w-full min-h-20"
                  placeholder="Describe what this workflow does..."
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Trigger</label>
                <select
                  value={newWorkflow.trigger}
                  onChange={(e) => setNewWorkflow({ ...newWorkflow, trigger: e.target.value })}
                  className="marketing-input w-full"
                  data-testid="workflow-trigger-select"
                >
                  {triggerTypes.map(t => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Actions</label>
                <div className="space-y-2">
                  {newWorkflow.actions.map((action, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-secondary rounded-full flex items-center justify-center text-xs text-muted-foreground">
                        {index + 1}
                      </span>
                      <select
                        value={action}
                        onChange={(e) => updateAction(index, e.target.value)}
                        className="marketing-input flex-1"
                      >
                        {actionTypes.map(a => (
                          <option key={a.id} value={a.id}>{a.label}</option>
                        ))}
                      </select>
                      {newWorkflow.actions.length > 1 && (
                        <button 
                          onClick={() => removeAction(index)}
                          className="p-2 hover:bg-red-500/20 rounded-lg"
                        >
                          <X className="w-4 h-4 text-red-400" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button 
                  onClick={addAction}
                  className="mt-2 text-sm text-primary flex items-center gap-1 hover:underline"
                >
                  <Plus className="w-4 h-4" />
                  Add Action
                </button>
              </div>
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Initial Status</label>
                <select
                  value={newWorkflow.status}
                  onChange={(e) => setNewWorkflow({ ...newWorkflow, status: e.target.value })}
                  className="marketing-input w-full"
                >
                  <option value="paused">Paused</option>
                  <option value="active">Active</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
              <button 
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="marketing-btn-secondary"
              >
                Cancel
              </button>
              <button 
                onClick={editingWorkflow ? handleUpdateWorkflow : handleCreateWorkflow}
                disabled={saving}
                className="marketing-btn-primary flex items-center gap-2"
                data-testid="save-workflow-btn"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingWorkflow ? 'Update Workflow' : 'Create Workflow'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
